import { DEFAULT_EQUALIZER, type EqualizerSettings } from '../settings';

// Audio boost and equalizer conversions for the Web Audio graph, ported from
// lib/utils/audio_boost_utils.dart. Pure functions. Display formatting
// (`+25%`, `1.5x`) lives in ui/format; settings store boost in percent.

export type { EqualizerSettings };

export const MIN_AUDIO_BOOST = 0;
export const DEFAULT_AUDIO_BOOST = 1;
export const MAX_AUDIO_BOOST = 2;
export const MAX_AUDIO_BOOST_GAIN_DB = 12;

export const MIN_EQ_BAND_DB = -12;
export const MAX_EQ_BAND_DB = 12;
export const MIN_EQ_PREAMP_DB = -12;
export const MAX_EQ_PREAMP_DB = 12;
export const MIN_EQ_BALANCE = -10;
export const MAX_EQ_BALANCE = 10;

/**
 * Flutter defines labels but no centre frequencies (its equalizer is UI only).
 * These follow spec 07 §21.2: shelving filters at the ends, peaking in between.
 */
export const EQ_BANDS = [
  { label: 'Bass', type: 'lowshelf', frequency: 100 },
  { label: 'L-Mid', type: 'peaking', frequency: 400 },
  { label: 'Mid', type: 'peaking', frequency: 1000 },
  { label: 'U-Mid', type: 'peaking', frequency: 3000 },
  { label: 'Treble', type: 'highshelf', frequency: 8000 },
] as const satisfies ReadonlyArray<{ label: string; type: BiquadFilterType; frequency: number }>;

export const EQ_BAND_Q = 1;

const TOLERANCE = 0.001;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const finite = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/** Boost as Flutter's 0..2 level (1 = 100 %). */
export function normalizeAudioBoost(value: unknown): number {
  return clamp(finite(value, DEFAULT_AUDIO_BOOST), MIN_AUDIO_BOOST, MAX_AUDIO_BOOST);
}

/** `settings.audioBoost` (percent, 100 = unchanged) → 0..2 level. */
export function boostFromPercent(percent: unknown): number {
  return normalizeAudioBoost(finite(percent, 100) / 100);
}

/** Gain in dB for the part of the boost above 100 % (1.5 → 6 dB, 2.0 → 12 dB). */
export function audioBoostToDecibelGain(value: number): number {
  const n = normalizeAudioBoost(value);
  if (n <= DEFAULT_AUDIO_BOOST) return 0;
  return ((n - DEFAULT_AUDIO_BOOST) / (MAX_AUDIO_BOOST - DEFAULT_AUDIO_BOOST)) * MAX_AUDIO_BOOST_GAIN_DB;
}

export function decibelsToGain(db: number): number {
  return 10 ** (db / 20);
}

/**
 * Linear gain for the boost stage: attenuation below 100 % is linear
 * (Flutter's `setVolume(min(v, 1))`), amplification above it follows the dB
 * mapping Flutter feeds Android's LoudnessEnhancer.
 */
export function boostToLinearGain(value: number): number {
  const n = normalizeAudioBoost(value);
  return n <= DEFAULT_AUDIO_BOOST ? n : decibelsToGain(audioBoostToDecibelGain(n));
}

export function normalizeEqualizer(raw: unknown): EqualizerSettings {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof EqualizerSettings, unknown>>;
  const bands = Array.isArray(src.bands) ? src.bands : [];
  return {
    preamp: clamp(finite(src.preamp, DEFAULT_EQUALIZER.preamp), MIN_EQ_PREAMP_DB, MAX_EQ_PREAMP_DB),
    balance: clamp(finite(src.balance, DEFAULT_EQUALIZER.balance), MIN_EQ_BALANCE, MAX_EQ_BALANCE),
    bands: [0, 1, 2, 3, 4].map((i) => clamp(finite(bands[i], 0), MIN_EQ_BAND_DB, MAX_EQ_BAND_DB)) as EqualizerSettings['bands'],
  };
}

export function isEqualizerFlat(eq: EqualizerSettings): boolean {
  return Math.abs(eq.preamp) < TOLERANCE && Math.abs(eq.balance) < TOLERANCE && eq.bands.every((b) => Math.abs(b) < TOLERANCE);
}

export function equalizerEquals(a: EqualizerSettings, b: EqualizerSettings): boolean {
  return a.preamp === b.preamp && a.balance === b.balance && a.bands.every((v, i) => v === b.bands[i]);
}
